import { Component, Inject, OnInit, Optional } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ContentTypeService } from '../../service/content-type.service';
import { CommentsService } from '../../service/comments.service';
import { SettingsService } from '../../service/settings.service';
import { TranslateService } from '@ngx-translate/core';
import { languagesList, languagesList_ } from 'na-lib';
import { deepCopy } from 'na-lib';
import { DppSchemaPreset } from '../dpp-schema-presets';
import { SchemaNode } from '../../service/excel-import.service';
import { EMPTY_SCHEMA_META_TEMPLATE, getMissingMandatoryMetaFields, SchemaMetaTemplate } from '../schema-meta.model';
import { FlattenedSchemaNode, SchemaEditorService } from '../schema-editor.service';

interface UnavailableSchemaPreset {
  id: string;
  title: string;
  descriptionKey: string;
  icon: string;
}

@Component({
    selector: 'app-create-content-type-dialog',
    templateUrl: './create-content-type-dialog.component.html',
    styleUrls: ['./create-content-type-dialog.component.css'],
    standalone: false
})
export class CreateContentTypeDialogComponent implements OnInit{
  newContentForm!:FormGroup
  languagesList:any = languagesList
  accountLanguages:any
  // Kept for the future preset-selection flow. No preset is currently active or loaded.
  schemaPresets: DppSchemaPreset[] = [];
  readonly unavailableSchemaPresets: UnavailableSchemaPreset[] = [
    {
      id: 'battery-dpp',
      title: 'Battery DPP',
      descriptionKey: 'contentTypes.wizard.batteryPresetDescription',
      icon: 'battery_charging_full'
    },
    {
      id: 'textile-dpp',
      title: 'Textile DPP',
      descriptionKey: 'contentTypes.wizard.textilePresetDescription',
      icon: 'checkroom'
    }
  ];
  currentStep = 1;
  selectedSchemaOptions: DppSchemaPreset['id'][] = [];
  workingSchema: SchemaNode[] = [];
  creating = false;
  metaTemplate: SchemaMetaTemplate = deepCopy(EMPTY_SCHEMA_META_TEMPLATE);

  constructor(
    public dialogRef: MatDialogRef<CreateContentTypeDialogComponent>,
    private fb: FormBuilder,
    private contentTypeService: ContentTypeService,
    private settingsService: SettingsService,
    private commentService: CommentsService,
    private translate:TranslateService,
    private schemaEditor: SchemaEditorService,
    @Optional() @Inject(MAT_DIALOG_DATA) public data: any
  ){
    dialogRef.disableClose = true;
    const currentLang = this.translate.currentLang() ?? 'en';
    this.languagesList = deepCopy(languagesList_[currentLang])
  }

  ngOnInit(): void {
    this.accountLanguages = this.settingsService.languagesContexts$
    this.newContentForm = this.fb.group({
      displayName:[,Validators.required],
      language: [this.accountLanguages.length > 0 ? this.accountLanguages[0] : '', Validators.required]
    })
  }

  get legacyCreateMode(): boolean {
    return this.data?.legacyCreate === true;
  }

  get selectedSchemaFieldCount(): number {
    return this.countFields(this.workingSchema || []);
  }

  get flattenedSchemaFields(): Array<SchemaNode & { path: string; level: number; indexPath: number[] }> {
    return this.flattenSchema(this.workingSchema || []);
  }

  selectPreset(presetId: DppSchemaPreset['id']): void {
    this.selectedSchemaOptions = this.selectedSchemaOptions.includes(presetId)
      ? this.selectedSchemaOptions.filter(id => id !== presetId)
      : [...this.selectedSchemaOptions, presetId];
    this.applySelectedPresets();
  }

  startCustomSchema(): void {
    this.selectedSchemaOptions = [];
    this.workingSchema = [];
  }

  goToStep(step: number): void {
    if (step < this.currentStep) this.currentStep = step;
  }

  nextFromBasics(): void {
    if (!this.newContentForm.valid) {
      this.newContentForm.markAllAsTouched();
      const message = this.translate.instant("SnackBarConstants.MANDATORY_FIELDS_EMPTY");
      this.commentService.addSnackBar.emit(message);
      return;
    }
    this.currentStep = 2;
  }

  get embeddedSchemaContentType(): any {
    return {
      id: 'new-content-type',
      displayName: this.newContentForm.value.displayName || this.translate.instant('contentTypes.wizard.newTemplate'),
      schema: { type: 'object', properties: deepCopy(this.workingSchema) },
      metaTemplate: deepCopy(this.metaTemplate)
    };
  }

  applySchemaFromEditor(result: [boolean, any, SchemaMetaTemplate]): void {
    if (!result?.[0]) return;
    this.workingSchema = this.schemaEditor.normalizeNodes(result[1]?.properties || []);
    this.metaTemplate = deepCopy(result[2] || EMPTY_SCHEMA_META_TEMPLATE);
  }

  cancelNewContentType(){
    this.dialogRef.close([false]);
  }

  async saveNewContentType(openPassTemplate = false) {
    if (this.newContentForm.value.displayName && this.newContentForm.value.language) {
      try {
        const missingMetaFields = getMissingMandatoryMetaFields(this.workingSchema, this.metaTemplate);
        if (missingMetaFields.length) {
          this.commentService.addSnackBar.emit(this.translate.instant('contentTypes.mandatoryMetaMissing', {
            fields: `${missingMetaFields.slice(0, 3).join(', ')}${missingMetaFields.length > 3 ? ' ...' : ''}`
          }));
          return;
        }
        const language = this.newContentForm.value.language;
        let updatedLanguages = [language, ...this.accountLanguages];
  
        // Ensure the array contains only unique values
        updatedLanguages = Array.from(new Set(updatedLanguages));
  
        const payload = this.legacyCreateMode
          ? {
              ...this.newContentForm.value,
              language: updatedLanguages
            }
          : {
              ...this.newContentForm.value,
              language: updatedLanguages,
              schema: { type: 'object', properties: this.getSelectedSchemaProperties() },
              metaTemplate: this.metaTemplate.fields.length ? this.metaTemplate : undefined,
              schemaMode: 'warning'
            };
    
        this.creating = true;
        this.commentService.progressSpin.emit(true);
  
        const newContent = await this.contentTypeService.createContentType(payload);
        const contypeId = newContent.contentTypeId;
        const passid = newContent.passTemplateId;
        const newRoute = passid ? 'home/' + this.settingsService.contextId$ + '/contentTypes/' + contypeId + '/passTemplate/' + passid : undefined;
        this.dialogRef.close([true, newContent, newRoute, { openPassTemplate }]);
  
        this.commentService.progressSpin.emit(false);
        const message = this.translate.instant("SnackBarConstants.CREATE_OK");
        this.commentService.addSnackBar.emit(message);
  
      } catch (error) {
        this.creating = false;
        this.commentService.progressSpin.emit(false);
        const message = this.translate.instant("SnackBarConstants.CREATE_FAILED");
        this.commentService.addSnackBar.emit(message);
      }
    } else {
      const message = this.translate.instant("SnackBarConstants.MANDATORY_FIELDS_EMPTY");
      this.commentService.addSnackBar.emit(message);
    }
  }

  private getSelectedSchemaProperties(): SchemaNode[] {
    return this.normalizeSchemaNodes(this.workingSchema || []);
  }

  isPresetSelected(presetId: DppSchemaPreset['id']): boolean {
    return this.selectedSchemaOptions.includes(presetId);
  }

  get isCustomSchemaSelected(): boolean {
    return !this.selectedSchemaOptions.length;
  }

  private applySelectedPresets(): void {
    const selectedSchema = this.selectedSchemaOptions.reduce<SchemaNode[]>((schema, presetId) => {
      const preset = this.schemaPresets.find(item => item.id === presetId);
      return this.mergeSchemaNodes(schema, deepCopy(preset?.properties || []));
    }, []);
    this.workingSchema = this.normalizeSchemaNodes(selectedSchema);
  }

  private mergeSchemaNodes(target: SchemaNode[], source: SchemaNode[]): SchemaNode[] {
    const merged = deepCopy(target || []);
    source.forEach(sourceNode => {
      const existingNode = merged.find(node => node.key === sourceNode.key);
      if (!existingNode) {
        merged.push(sourceNode);
        return;
      }
      const sourceChildren = sourceNode.properties || sourceNode.items?.properties || [];
      const existingChildren = existingNode.properties || existingNode.items?.properties || [];
      if (sourceChildren.length && existingChildren.length) {
        const mergedChildren = this.mergeSchemaNodes(existingChildren, sourceChildren);
        if (existingNode.properties) existingNode.properties = mergedChildren;
        if (existingNode.items?.properties) existingNode.items.properties = mergedChildren;
      }
    });
    return merged;
  }

  private countFields(nodes: SchemaNode[]): number {
    return this.schemaEditor.countFields(nodes);
  }

  getFieldIndent(field: { level?: number }): number {
    return 12 + (Number(field?.level) || 0) * 18;
  }

  private flattenSchema(nodes: SchemaNode[]): FlattenedSchemaNode[] {
    return this.schemaEditor.flatten(nodes);
  }

  private normalizeSchemaNodes(nodes: SchemaNode[]): SchemaNode[] {
    return this.schemaEditor.normalizeNodes(nodes);
  }
  
}
